import React, { useState, useEffect, useRef } from 'react';
import api from './api/axiosInstance';
import Sidebar from './Sidebar.js';
import { Card, Row, Col, Container, Button, Form, Modal, Alert, Image} from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import CityAddGeneral from './CityAddGeneral';
import CityOfficeDesk from './CityOfficeDesk';
import Breadcrumb from './Breadcrumb.js';
import NavTabs from './NavTabs';
import CityGeneralInfo from './CityGeneralInfo.js';
import CityCompaniesCard from './CityCompaniesCard.js';
import { TicketCountContext } from './TicketCountContext';
import icSucess from './assets/img/ic-celebration-circle.svg';
import { exportCityPdf } from './api/exportCityPdf.js';
import { MAPBOX_TOKEN } from './config/mapbox';

const CityEdit = () => {
    const { id } = useParams();
    const { ticketCount } = React.useContext(TicketCountContext);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(true);
    const [geocodeLoading, setGeocodeLoading] = useState(false);
    const [geocodeError, setGeocodeError] = useState('');
    const lastGeocodedKeyRef = useRef('');
    const [showDeleteModal, setShowDeleteModal] = useState(false); // Delete modal state
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [showSavedModal, setShowSavedModal] = useState(false);
    const navigate = useNavigate();

    const userNavItems = [
        { to: '/city-edit/:id', eventKey: 'cityInfo', label: 'About' },
        { to: '/city-edit-companies/:id', eventKey: 'companies', label: 'Companies & Jobs', alsoActive: [`/city-edit-jobs/${id}`] },
        { to: '/city-edit-salary/:id', eventKey: 'salariesInfo', label: 'Average salaries' },
        { to: '/city-edit-insights/:id', eventKey: 'cityInsights', label: 'City insights' },
        { to: '/city-edit-universities/:id', eventKey: 'universities', label: 'Universities' },
        { to: '#', eventKey: 'cityComments', label: 'Comments' },
        { to: '/city-edit-history/:id', eventKey: 'history', label: 'History of changes' },
    ];

    const [formData, setFormData] = useState({
        name: '',
        continent: '',
        country: {},
        citizensNumber: '',
        regionCitizensNumber: '',
        cityId: '',
        url: '',
        languagesSpoken: {},
        cityRegionDescription: '',
        officeUrl: '',
        address: '',
        phone: '',
        workingHours: [{ fromDay: '', toDay: '', fromTime: '', toTime: '' }],
        govUrl: '',
        latitude: '',
        longitude: '',
    });

    const normalizeHttpUrl = (s) => {
        if (!s) return '';
        try {
            const hasProto = /^https?:\/\//i.test(s);
            const u = new URL(hasProto ? s : `https://${s}`);
            return u.href;
        } catch {
            return ''; // invalid -> will fail validation
        }
    };

    useEffect(() => {
        const fetchCity = async () => {
            try {
                const response = await api.get(`/api/cities/${id}`);

                const data = response.data;

                // ✅ Ensure fallback/defaults are safe
                setFormData({
                    name: data.name || '',
                    continent: data.continent || '',
                    country: (data.country && typeof data.country === 'object') ? data.country : {},
                    citizensNumber: data.citizensNumber || '',
                    regionCitizensNumber: data.regionCitizensNumber || '',
                    cityId: data.cityId || '',
                    url: data.url || '',
                    languagesSpoken: Array.isArray(data.languagesSpoken) ? data.languagesSpoken : [],
                    cityRegionDescription: data.cityRegionDescription || '',
                    universitiesNumber: Array.isArray(data.universities)
                        ? data.universities.length
                        : 0,
                    studentsNumber: Array.isArray(data.universities)
                        ? data.universities.reduce((sum, uni) => {
                            // handle null / undefined / string values safely
                            const value =
                                typeof uni.studentsNumber === 'number'
                                    ? uni.studentsNumber
                                    : parseInt(uni.studentsNumber, 10) || 0;
                            return sum + value;
                        }, 0)
                        : 0,
                    officeUrl: data.officeUrl || '',
                    address: data.address || '',
                    phone: data.phone || '',
                    workingHours: Array.isArray(data.workingHours) && data.workingHours.length
                        ? data.workingHours
                        : [{ fromDay: '', toDay: '', fromTime: '', toTime: '' }],
                    govUrl: data.govUrl || '',
                    latitude: data.latitude ?? '',
                    longitude: data.longitude ?? '',
                });

                setLoading(false); // ✅ Done loading
            } catch (error) {
                console.error('Error fetching city:', error);
                setLoading(false); // ✅ Avoid infinite spinner
            }
        };

        fetchCity();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        console.log('handleChange called with:', name, value);
    
        // Check if the incoming change is for the country object and handle accordingly
        if (name === 'country' && typeof value === 'object') {
            setFormData(prevData => ({
                ...prevData,
                country: { ...value }  // Update the country object properly
            }));
        } else {
            setFormData(prevData => ({
                ...prevData,
                [name]: value  // This assumes value is always correct
            }));
        }
    };

    const geocodeCityCoordinates = async ({
        cityName = formData.name,
        countryName = formData.country?.name,
        force = false,
    } = {}) => {
        const trimmedCity = String(cityName || '').trim();
        const trimmedCountry = String(countryName || '').trim();
        const mapboxToken = MAPBOX_TOKEN;

        if (!trimmedCity || !trimmedCountry) return false;
        if (!mapboxToken) { setGeocodeError('Mapbox token is missing.'); return false; }

        const lookupKey = `${trimmedCity.toLowerCase()}__${trimmedCountry.toLowerCase()}`;

        if (!force && lastGeocodedKeyRef.current === lookupKey && formData.latitude && formData.longitude) {
            return true;
        }

        setGeocodeLoading(true);
        setGeocodeError('');

        try {
            const query = encodeURIComponent(`${trimmedCity}, ${trimmedCountry}`);
            const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?types=place&limit=1&access_token=${mapboxToken}`
            );
            const data = await response.json();
            const center = data?.features?.[0]?.center;

            if (!Array.isArray(center) || center.length < 2) {
                setGeocodeError('Could not detect city coordinates automatically.');
                setFormData(prev => ({ ...prev, latitude: '', longitude: '' }));
                return false;
            }

            const [lng, lat] = center;
            setFormData(prev => ({ ...prev, latitude: Number(lat), longitude: Number(lng) }));
            setErrors(prev => ({ ...prev, latitude: '', longitude: '' }));
            lastGeocodedKeyRef.current = lookupKey;
            return true;
        } catch (error) {
            console.error('City geocoding failed:', error);
            setGeocodeError('Could not detect city coordinates automatically.');
            return false;
        } finally {
            setGeocodeLoading(false);
        }
    };

    const validateGeneral = () => {
        const newErrors = {};

        if (!formData.name) newErrors.name = 'City name is required.';
        if (!formData.continent || formData.continent.trim() === '') newErrors.continent = 'Continent is required.';
        if (!formData.country || !formData.country.id) newErrors.country = 'Country is required.';
        if (!formData.citizensNumber) newErrors.citizensNumber = 'City citizen number is required.';
        if (!formData.regionCitizensNumber) newErrors.regionCitizensNumber = 'Region citizen number is required.';
        if (!formData.cityId) newErrors.cityId = 'City ID is required.';
       
        if (!formData.address) newErrors.address = 'Address is required.';
        if (!formData.phone) newErrors.phone = 'Phone is required.';

        const officeCandidate = formData.officeUrl?.trim() || '';
        const officeNormalized = normalizeHttpUrl(officeCandidate);
        if (!officeCandidate || !officeNormalized) {
            newErrors.officeUrl = 'Citizen office desk URL must be a valid http(s) URL.';
        }

        const candidateUrl = formData.url?.trim() || '';
        const normalized = normalizeHttpUrl(candidateUrl);
        if (!candidateUrl || !normalized) {
            newErrors.url = 'Regional URL is required and must be a valid http(s) URL.';
        }

        setErrors(newErrors);
        console.log('validateGeneral errors:', newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const submitGeneral = async () => {
        if (!validateGeneral()) {
            setShowErrorMessage(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return false;
        }

        const payload = {
            name: formData.name,
            continent: formData.continent,
            country: formData.country,
            citizensNumber: formData.citizensNumber,
            regionCitizensNumber: formData.regionCitizensNumber,
            cityId: formData.cityId,
            url: normalizeHttpUrl(formData.url),
            languagesSpoken: formData.languagesSpoken,
            cityRegionDescription: formData.cityRegionDescription,
            officeUrl: normalizeHttpUrl(formData.officeUrl),
            address: formData.address,
            phone: formData.phone,
            workingHours: formData.workingHours,
            govUrl: formData.govUrl,
            latitude: formData.latitude !== '' ? Number(formData.latitude) : null,
            longitude: formData.longitude !== '' ? Number(formData.longitude) : null,
        };

        try {
            console.log('🔍 CityEdit – submitting payload:', payload); // ✅ add this

            const response = await api.put(
                `/api/cities/${id}`,
                payload,
            );
            console.log('City update response:', response.data); // 🟢 debug
            setShowErrorMessage(false);
            setShowSavedModal(true);
            return true;
        } catch (error) {
            console.error('Error updating city:', error);
            setErrors(prev => ({
                ...prev,
                general: error.response?.data?.message || 'An error occurred while updating the city. Please try again.',
            }));
            setShowErrorMessage(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return false;
        }
    };

    const handleGeneralSubmit = async (e) => {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        return submitGeneral();
    };

    const handleExportPdf = async () => {
        try {
            await exportCityPdf(id);
        } catch (error) {
            console.error('Error exporting city PDF:', error);
            alert('An error occurred while exporting the city PDF.');
        }
    };

    const handleDeleteCity = () => {
        setShowDeleteModal(true);
    };

    const confirmDeleteCity = async () => {
        try {
            await api.delete(`/api/cities/${id}`);
            setShowDeleteModal(false);
            navigate('/onboarding-city-list'); // Redirect to company list after deletion
        } catch (error) {
            console.error('Error deleting city:', error);
            alert('An error occurred while deleting the city.');
        }
    };

    return (
        <>
            <Sidebar ticketCount={ticketCount} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            {showErrorMessage && (
                                <Alert variant="danger" onClose={() => setShowErrorMessage(false)} dismissible>
                                    <p><strong>Error</strong> - There are items that require your attention</p>
                                </Alert>
                            )}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Breadcrumb cityName={formData.name} />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Card>
                                <Card.Body className="pt-0">
                                    <Row>
                                        <Col xs={12}>
                                            <>
                                                <Row>
                                                    <Col>
                                                        <CityGeneralInfo
                                                            formData={formData}
                                                        />
                                                    </Col>
                                                </Row>
                                                <Row className="mb-5">
                                                    <Col>
                                                        <NavTabs items={userNavItems} />
                                                    </Col>
                                                </Row>
                                                <Form onSubmit={handleGeneralSubmit}>
                                                    <Row>
                                                        <Col>
                                                            <CityAddGeneral
                                                                formData={formData}
                                                                handleChange={handleChange}
                                                                errors={errors}
                                                                isEditPage={true}
                                                                handleGeneralSubmit={handleGeneralSubmit}
                                                                geocodeLoading={geocodeLoading}
                                                                geocodeError={geocodeError}
                                                                onRefreshCoordinates={() => geocodeCityCoordinates({ force: true })}
                                                            />
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col>
                                                            <CityOfficeDesk
                                                                formData={formData}
                                                                handleChange={handleChange}
                                                                errors={errors}
                                                                isEditPage={true}
                                                                handleGeneralSubmit={handleGeneralSubmit}
                                                                onShowErrorMessage={setShowErrorMessage}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </Form>
                                                {!loading && formData.name && (
                                                    <CityCompaniesCard cityName={formData.name} />
                                                )}
                                            </>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row className="buttons-row">
                        <Col>
                            <Button variant="outline-secondary" className="ms-3" onClick={() => navigate('/onboarding-city-list')}>
                                Cancel
                            </Button>
                            <Button variant="outline-danger" onClick={handleDeleteCity}>
                                Delete City
                            </Button>
                            <Button variant="outline-secondary" onClick={handleExportPdf}>
                                Download city report
                            </Button>
                        </Col>
                    </Row>
                
                </Container>
            </main>
                                    
            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete this city? This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary me-3" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="outline-danger" onClick={confirmDeleteCity}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
            {/* Saved info Modal */}
            <Modal show={showSavedModal} onHide={() => setShowSavedModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Image
                        src={icSucess}
                        alt=""
                        className="d-block m-auto mb-4"
                    />
                    <h2 className="text-center mb-3">Update successful</h2>
                    <p className="text-center">City info Updated</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowSavedModal(false)}>OK</Button>
                </Modal.Footer>
            </Modal>

        </>
    );
};

export default CityEdit;
